import { useSelector } from 'react-redux';
import { Box, CloseButton, HStack, Progress } from '@chakra-ui/react';
import { Alert, AlertIcon, Button, Text } from '@/components/v4';
import { STATUS_BANNER_HEIGHT } from '../constants';
import { getRemovingTrunksLabel, getRunSummary } from '../utils/bulkUpdate';

const plural = (count, word) => `${count} ${count === 1 ? word : `${word}s`}`;

const getStatus = (run, isRunning) => {
    if (isRunning) return 'info';
    return run.failed.length > 0 || run.notSent > 0 ? 'warning' : 'success';
};

const getHeadline = (run, isRunning) => {
    if (run.isCancelling) return 'Cancelling bulk update...';
    if (isRunning && run.removingTrunks > 0) {
        return getRemovingTrunksLabel(run.removingTrunks);
    }
    if (isRunning) {
        return `Updating ${run.processed.toLocaleString()} of ${run.total.toLocaleString()} numbers`;
    }
    const outcome = run.notSent > 0 ? 'cancelled' : 'finished';
    return `Bulk update ${outcome}: ${getRunSummary(run)}`;
};

const getNote = (run, isRunning) => {
    if (run.isCancelling) {
        return "Finishing the updates already sent. The rest won't be sent.";
    }
    if (isRunning) {
        return 'Refreshing or leaving this page will cancel the remaining updates.';
    }
    if (run.failed.length > 0) {
        return `${plural(run.failed.length, 'number')} could not be updated. View results to select them again.`;
    }
    if (run.notSent > 0) {
        return "Numbers that weren't sent have been left unchanged.";
    }
    return 'All selected numbers have been processed.';
};

/**
 * Banner above the lists while the Bulk Update progress dialog is hidden:
 * progress and a warning while the run is going, then its result. Fixed
 * height, so the lists shrink by a set amount rather than jumping around.
 */
const BulkUpdateStatus = ({ run, onView, onCancel, onDismiss }) => {
    const { colorScheme } = useSelector((state) => state.settings);
    if (run.status === 'idle') return null;

    const isRunning = run.status === 'running';
    const percent = run.total ? (run.processed / run.total) * 100 : 0;

    return (
        <Alert
            status={getStatus(run, isRunning)}
            variant="inline-accent"
            h={STATUS_BANNER_HEIGHT}
            mb={4}
            aria-live="polite">
            <AlertIcon />
            <Box flex="1" minW={0}>
                <HStack spacing={3}>
                    <Text
                        fontSize="sm"
                        fontWeight="500"
                        whiteSpace="nowrap"
                        m={0}>
                        {getHeadline(run, isRunning)}
                    </Text>
                    {isRunning ? (
                        <Progress
                            value={percent}
                            colorScheme={colorScheme}
                            size="sm"
                            flex="1"
                            borderRadius="md"
                            hasStripe
                            isAnimated
                            isIndeterminate={run.removingTrunks > 0}
                            aria-label="Update progress"
                        />
                    ) : (
                        <Box flex="1" />
                    )}
                    <Button
                        variant="link"
                        size="sm"
                        flexShrink={0}
                        onClick={onView}>
                        {isRunning ? 'View progress' : 'View results'}
                    </Button>
                    {isRunning && (
                        <Button
                            variant="outline"
                            colorScheme="red"
                            size="sm"
                            flexShrink={0}
                            isLoading={run.isCancelling}
                            loadingText="Cancelling"
                            onClick={onCancel}>
                            Cancel Update
                        </Button>
                    )}
                    {!isRunning && (
                        <CloseButton
                            size="sm"
                            aria-label="Dismiss update result"
                            onClick={onDismiss}
                        />
                    )}
                </HStack>
                <Text fontSize="xs" mt={1} mb={0}>
                    {getNote(run, isRunning)}
                </Text>
            </Box>
        </Alert>
    );
};

export default BulkUpdateStatus;
